import { AVAILABILITY_FILTER_KEY_V2 } from 'elements/hardwareFilter/stockAvailability/AvailabilityFilterNodeFactoryV2'
import { DISTRIBUTOR_FILTER_KEY_V2 } from 'elements/hardwareFilter/distributor/constants'
import useHardwareSelectorFetchEngine from 'elements/hardwareSelectorV2/fetch/useHardwareSelectorFetchEngine'
import listQueryParamsFormatter from 'elements/input/listQueryParamsFormatter'
import { useTranslate } from 'ra-core'
import { useMemo } from 'react'
import type { ComponentTypesV2 } from 'types/selectComponent'
import { useShopCategoryConfig, useSupplierScopedCategories, ShopCategory } from '../categoryConfig'
import { HardwareSupplierFilterKeyType } from '../type'
import ProductGrid from './ProductGrid'

const SHOP_CATALOG_PERSISTENT = { referrer: 'shop' as const }
const PAGE_SIZE = 10

type SupplierCategoryCarouselProps = {
  category: ShopCategory
  distributor: HardwareSupplierFilterKeyType
  defaultFilterValues: Record<string, unknown>
}

const SupplierCategoryCarousel = ({ category, distributor, defaultFilterValues }: SupplierCategoryCarouselProps) => {
  const translate = useTranslate()
  const { getSubtypesForCategory } = useShopCategoryConfig()

  const componentTypes = useMemo((): ComponentTypesV2[] => {
    if (category.componentType === 'other') {
      return getSubtypesForCategory(category) as ComponentTypesV2[]
    }
    return [category.componentType]
  }, [category, getSubtypesForCategory])

  const { componentsData, loading, loaded } = useHardwareSelectorFetchEngine({
    componentTypes,
    defaultFilterValues,
    limit: PAGE_SIZE,
    sortBy: '_distributor_count',
    persistentFilterValuesOverride: SHOP_CATALOG_PERSISTENT,
  })

  if (loaded && (componentsData?.length ?? 0) === 0) {
    return null
  }

  const viewAllLink = `/shop/search/${category.searchKey}/supplier/${distributor}`
  const productGridCategory = category.componentType === 'other' ? 'other' : category.componentType

  return (
    <ProductGrid
      carouselMode={true}
      componentsData={componentsData}
      title={translate(`Shop ${category.name}`)}
      viewAllLink={viewAllLink}
      category={productGridCategory}
      loading={loading && !loaded}
      isFetching={loading}
      pageSize={PAGE_SIZE}
    />
  )
}

type SupplierCategoryCarouselsProps = {
  distributor: HardwareSupplierFilterKeyType
}

const SupplierCategoryCarousels = ({ distributor }: SupplierCategoryCarouselsProps) => {
  const visibleCategories = useSupplierScopedCategories(distributor)

  const defaultFilterValues = useMemo(
    () => ({
      [DISTRIBUTOR_FILTER_KEY_V2]: listQueryParamsFormatter.format({ value: new Set([distributor]) }),
      [AVAILABILITY_FILTER_KEY_V2]: 'true',
    }),
    [distributor]
  )

  return (
    <>
      {visibleCategories.map((category) => (
        <SupplierCategoryCarousel
          key={category.id}
          category={category}
          distributor={distributor}
          defaultFilterValues={defaultFilterValues}
        />
      ))}
    </>
  )
}

export default SupplierCategoryCarousels
